import { getCookie, getGAClientId } from './tracking';
import { computePerfil } from './segmentation';
import type { FormAnswers, UtmParams } from '../types';

interface SubmitParams {
  answers: FormAnswers;
  utmParams: UtmParams;
  countryDetected: string;
}

// Carril del embudo (p), variante del gancho (v) e identidad elegida en el VSL
// era-mentor (f: coach / mentor / terapeuta / conocimiento) que trae el visitante
// desde el vídeo. Se leen de la URL y, como respaldo, de localStorage (lo persiste
// useUtmTracking al cargar, por si el router quita el query antes del envío). Se
// reenvían a GHL para poder leer coste/agenda × cierre POR CARRIL y POR IDENTIDAD;
// el workflow de GHL los mapea a campos/tags del contacto. Fail-open: si falla, va vacío.
function getCarril(): Record<string, string> {
  const out: Record<string, string> = {};
  try {
    const u = new URLSearchParams(window.location.search);
    const p = u.get('p');
    const v = u.get('v');
    const f = u.get('f');
    if (p) out.p = p;
    if (v) out.v = v;
    if (f) out.f = f;
    if (out.p || out.v || out.f) return out;
    const raw = localStorage.getItem('hit_carril_data');
    if (raw) return JSON.parse(raw) as Record<string, string>;
  } catch {
    /* fail-open */
  }
  return out;
}

export async function submitApplication({
  answers,
  utmParams,
  countryDetected,
}: SubmitParams): Promise<{ success: boolean; error?: string }> {
  const payload = {
    source: 'hit-application-form',
    timestamp: new Date().toISOString(),
    answers,
    perfil: computePerfil(answers),
    utm: utmParams,
    carril: getCarril(),
    meta: {
      user_agent: navigator.userAgent,
      referrer: document.referrer,
      fbp: getCookie('_fbp'),
      fbc: getCookie('_fbc'),
      ga_client_id: getGAClientId(),
      country_detected: countryDetected,
      language: navigator.language,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      submitted_at: new Date().toISOString(),
    },
  };

  const MAX_RETRIES = 2;
  const RETRY_DELAY_MS = 1500;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const response = await fetch(import.meta.env.VITE_N8N_WEBHOOK_URL as string, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        localStorage.removeItem('hit_pending_submission');
        return { success: true };
      }

      throw new Error(`HTTP ${response.status}`);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      console.error(`Submit attempt ${attempt + 1} failed:`, msg);

      if (attempt === MAX_RETRIES) {
        localStorage.setItem(
          'hit_pending_submission',
          JSON.stringify({ payload, failed_at: new Date().toISOString() })
        );
        return { success: false, error: msg };
      }

      await new Promise(r => setTimeout(r, RETRY_DELAY_MS));
    }
  }

  return { success: false, error: 'Max retries exceeded' };
}
